import { Client, ClientStatus } from '../../context/ClientContext';
import type { BadgeTone } from '../ui';
import { formatDate } from '../../utils/dateUtils';

/**
 * Shared data helpers for the Clients screen — attention semantics,
 * status tones, relative dates and sorting. Pure functions only.
 */

export type AttentionTone = 'up' | 'warn' | 'info';

export interface Attention {
  tone: AttentionTone;
  label: string;
}

/**
 * Attention signal derived from today's tasks. Same semantics as the
 * legacy dot colors (blue=scheduled, yellow=mixed, green=awaiting review):
 * - pending > 0 && review > 0  -> warn  (partially done)
 * - pending === 0 && review > 0 -> up   (all done, awaiting review)
 * - pending > 0                 -> info (tasks scheduled)
 */
export function getAttention(client: Client): Attention | null {
  const pending = client.tasksDueToday;
  const review = client.tasksAwaitingReview;
  if (pending > 0 && review > 0) return { tone: 'warn', label: 'Partially done — review what is ready' };
  if (pending === 0 && review > 0) return { tone: 'up', label: 'All tasks done — awaiting review' };
  if (pending > 0) return { tone: 'info', label: 'Tasks scheduled for today' };
  return null;
}

export const ATTENTION_PRIORITY: Record<AttentionTone, number> = { up: 3, warn: 2, info: 1 };

export const STATUS_TONE: Record<ClientStatus, BadgeTone> = {
  Active: 'up',
  Prospect: 'info',
  Paused: 'warn',
  Archived: 'idle',
};

/** Clients page tabs: by the service a client buys (clients.service_seo / service_google_ads, Settings → Services). */
export const SERVICE_TABS = ['All', 'SEO', 'Google Ads'] as const;
export type ServiceTab = (typeof SERVICE_TABS)[number];

export type ClientSort = 'Attention' | 'Name' | 'Last Activity';

export const CLIENT_SORTS: ClientSort[] = ['Attention', 'Name', 'Last Activity'];

/** "Today" / "Yesterday" / "Mar 4" / "Mar 4, 2025" — em dash when unknown. */
export function relativeDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (d.getFullYear() === now.getFullYear()) return formatDate(d, 'MMM d');
  return formatDate(d, 'MMM d, yyyy');
}

export function isRecent(iso: string | null | undefined): boolean {
  const rel = relativeDate(iso);
  return rel === 'Today' || rel === 'Yesterday';
}

/**
 * Sort clients for the list views.
 * Attention (default): up(3) > warn(2) > info(1) > none, then name.
 * Last Activity: most recent first, missing dates last.
 */
export function sortClients(rows: Client[], sort: ClientSort): Client[] {
  const byName = (a: Client, b: Client) => a.name.localeCompare(b.name);
  const copy = [...rows];
  if (sort === 'Name') return copy.sort(byName);
  if (sort === 'Last Activity') {
    return copy.sort((a, b) => {
      const ta = a.lastActivityDate ? new Date(a.lastActivityDate).getTime() : null;
      const tb = b.lastActivityDate ? new Date(b.lastActivityDate).getTime() : null;
      if (ta === null && tb === null) return byName(a, b);
      if (ta === null) return 1;
      if (tb === null) return -1;
      return tb - ta || byName(a, b);
    });
  }
  return copy.sort((a, b) => {
    const attA = getAttention(a);
    const attB = getAttention(b);
    const pa = attA ? ATTENTION_PRIORITY[attA.tone] : 0;
    const pb = attB ? ATTENTION_PRIORITY[attB.tone] : 0;
    return pb - pa || byName(a, b);
  });
}
